import type { ComponentPublicInstance } from 'vue';

type TargetElement = HTMLElement | null;
type ComponentOrElement = HTMLElement | ComponentPublicInstance | null;
type TargetRef = Ref<TargetElement> | Ref<ComponentOrElement>;
type MaybeArray<T> = T | Array<T>;

interface OnClickOutsideOptions {
    /**
     * DOM events to listen for
     * @default ['mousedown']
     */
    events?: string[];

    /**
     * Whether to temporarily ignore click outside detection
     * @default false
     */
    ignore?: boolean;
}

interface OnClickOutsideControls {
    cleanup: () => void;
    start: () => void;
    stop: () => void;
}

/**
 * Extracts the DOM element from a ref value.
 * Handles both direct HTMLElement refs and Vue component refs (via useTemplateRef).
 */
function getElement(value: ComponentOrElement): HTMLElement | null {
    if (!value) return null;

    // Check if it's a Vue component instance (has $el property)
    if ('$el' in value) {
        return value.$el as HTMLElement;
    }

    // It's already an HTMLElement
    return value as HTMLElement;
}

/**
 * Detects clicks outside of specified elements and triggers a callback
 *
 * @param target - Element ref or array of element refs to detect clicks outside of.
 *                 Supports both HTMLElement refs and component refs from useTemplateRef.
 * @param callback - Function to call when a click outside is detected
 * @param options - Additional options
 * @returns Controls for the click outside detection
 */
export function useOnClickOutside(
    target: MaybeArray<TargetRef>,
    callback: (event: Event) => void,
    options: OnClickOutsideOptions = {}
): OnClickOutsideControls {
    const { events = ["mousedown"], ignore = false } = options;

    let mounted = false;

    const listener = (event: Event): void => {
        if (ignore) return;

        const targets = Array.isArray(target) ? target : [target];

        const isOutside = targets.every((targetRef) => {
            const el = getElement(unref(targetRef));
            if (!el) return true;

            return !el.contains(event.target as Node);
        });

        if (isOutside) {
            callback(event);
        }
    };

    const cleanup = (): void => {
        if (!mounted) return;

        events.forEach((event) => {
            document.removeEventListener(event, listener);
        });

        mounted = false;
    };

    onBeforeUnmount(cleanup);

    return {
        cleanup,
        start: () => {
            cleanup();
            mounted = true;
            events.forEach((event) => {
                document.addEventListener(event, listener);
            });
        },
        stop: cleanup,
    };
}
